// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddWishListLineToCart,
    getWishListLinesDataView,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import deleteWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishListLines";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import {
    CartLineCollectionModel,
    CartLineModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";

import AddNewItems from "@awc/App/Components/MyListsDetails/AddNewItems";
import { CustomAppState } from "@awc/App/Store/Reducers";
import TextField, { TextFieldProps } from "@awc/MobiusOverrides/TextField/TextField";
import { Dictionary } from "@insite/client-framework/Common/Types";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import { OrderUploadErrorsModalStyles } from "@insite/content-library/Components/OrderUploadErrorsModal";
import { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import { Button, Select, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    loadWishListLinesParameter: state.pages.myListDetails.loadWishListLinesParameter,
    selectedWishListLineIds: state.pages.myListDetails.selectedWishListLineIds,
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
    // XNG-Changes:
    productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
    isUpdatingCart: state.context.isUpdatingCart,
    isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
    setEditingSortOrder,
    // XNG-changes
    deleteWishListLines,
    addLinesToCart,
    addWishListToCart,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsOptionsStyles {
    wrapper?: InjectableCss;
    leftColumnWrapper?: InjectableCss;
    rightColumnWrapper?: InjectableCss;
    container?: GridContainerProps;
    selectAllGridItem?: GridItemProps;
    selectAll?: CheckboxProps;
    editSortOrder?: CheckboxProps;
    searchGridItem?: GridItemProps;
    addItemsGridItem?: GridItemProps;
    addItemsLink?: LinkPresentationProps;
    addItemsGridContainer?: GridContainerProps;
    productSelectorGridItem?: GridItemProps;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsGridItem?: GridItemProps;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    search?: TextFieldProps;
    sortByGridItem?: GridItemProps;
    sortByOverflowMenu?: OverflowMenuProps;
    sortByClickable?: ClickablePresentationProps;
    selectedSortByClickable?: ClickablePresentationProps;
    closeAddItemsToList?: IconProps;
    // XNG-Change
    topSectionWrapper?: InjectableCss;
    bottomSectionWrapper?: InjectableCss;
    leftSectionWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    addDeleteWrapper?: InjectableCss;
    deleteListItemsModal?: TwoButtonModalStyles;
    addSelectedToCartButton?: ButtonPresentationProps;
    deleteSelectedButton?: ButtonPresentationProps;
    editSortOrderLink?: LinkPresentationProps;
    sortBySelect?: SelectPresentationProps;
    listInfoWrapper?: InjectableCss;
    wishlistCountWrapper?: InjectableCss;
    wishlistLabel?: TypographyPresentationProps;
    wishlistCount?: TypographyPresentationProps;
    innerButtonWrapper?: InjectableCss;
    addListButton?: ButtonPresentationProps;
    itemsTotalWrapper?: InjectableCss;
}

export const myListsDetailsOptionsStyles: MyListsDetailsOptionsStyles = {
    wrapper: {
        css: css`
            @media print {
                display: none;
            }
            display: flex;
            margin: 20px 0;
        `,
    },
    leftColumnWrapper: {
        css: css`
            display: flex;
            width: calc(100% - 40px);
        `,
    },
    rightColumnWrapper: {
        css: css`
            display: flex;
            width: 40px;
            justify-content: center;
            margin-top: 30px;
        `,
    },
    container: {
        gap: 10,
        css: css`
            margin-top: 32px;
        `,
    },
    selectAllGridItem: {
        width: [12, 12, 6, 4, 3],
        css: css`
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: space-between;
                    `,
                    css`
                        justify-content: space-between;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    selectAll: {
        css: css`
            cursor: pointer;
        `,
    },
    editSortOrder: {
        css: css`
            cursor: pointer;
            margin-left: 30px;
        `,
    },
    searchGridItem: { width: [12, 12, 6, 4, 4] },
    addItemsGridItem: {
        width: [12, 12, 6, 4, 5],
        css: css`
            align-items: center;
        `,
    },
    addItemsLink: {
        icon: {
            iconProps: { src: PlusCircle },
        },
        typographyProps: {
            css: css`
                padding-left: 5px;
            `,
        },
    },
    addItemsGridContainer: { gap: 10 },
    productSelectorGridItem: {
        width: [12, 12, 12, 10, 10],
    },
    uploadItemsGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-top: 30px;
                    `,
                    css`
                        padding-top: 30px;
                    `,
                ])}
        `,
    },
    uploadItemsLink: {
        css: css`
            margin: 15px 0 0;
        `,
    },
    uploadItemsModal: {
        sizeVariant: "large",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 30px;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    search: {
        iconProps: { src: Search },
    },
    sortByOverflowMenu: {
        iconProps: { src: ChevronsUpDown },
    },
    selectedSortByClickable: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    closeAddItemsToList: {
        src: X,
        css: css`
            margin: 8px;
            cursor: pointer;
            align-items: flex-start;
        `,
    },
};

const styles = myListsDetailsOptionsStyles;

const MyListsDetailsOptions = ({
    id,
    wishListSettings,
    wishListDataView,
    wishListLinesDataView,
    loadWishListLinesParameter,
    selectedWishListLineIds,
    editingSortOrder,
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList,
    setEditingSortOrder,
    // XNG-Changes
    productInfosByWishListLineId,
    isUpdatingCart,
    addLinesToCart,
    deleteWishListLines,
    addWishListToCart,
    isMobileScreen,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [query, setQuery] = useState(loadWishListLinesParameter.query || "");
    // const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    // const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    // XNG-Changes
    const [deleteListItemsModalIsOpen, setDeleteListItemsModalIsOpen] = useState(false);
    const editSortOrderText = editingSortOrder ? translate("Finish Editing Sort Order") : translate("Edit Sort Order");

    useEffect(() => {
        setQuery("");
        setEditingSortOrder({ editingSortOrder: false });
    }, []);

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!wishListDataView.value) {
                return;
            }

            const searchText = query.trim();
            updateLoadWishListLinesParameter({ query: searchText });
            loadWishListLines();
        }, 200),
        [wishListDataView],
    );

    if (!wishListDataView.value || !wishListSettings) {
        return null;
    }

    const wishList = wishListDataView.value;
    // XNG-Changes
    const showAddToCart = (wishListLinesDataView.value?.length || 0) > 0;

    const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value || "";
        setQuery(newQuery);
        debouncedSearch(newQuery);
    };

    const sortByChangeHandler = (sortBy: string) => {
        setEditingSortOrder({ editingSortOrder: false });
        updateLoadWishListLinesParameter({ sort: sortBy });
        loadWishListLines();
    };

    const isAllSelected =
        wishListLinesDataView.value &&
        wishListLinesDataView.value.length > 0 &&
        wishListLinesDataView.value.every(o => selectedWishListLineIds.indexOf(o.id) >= 0);

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        const allWishListLineIds = value ? wishListLinesDataView?.value?.map(o => o.id) : undefined;
        setAllWishListLinesIsSelected({ isSelected: value, wishListLineIds: allWishListLineIds });
    };

    const addAllToCartClickHandler = (e: any) => {
        e.preventDefault();
        if (!wishList || !wishListLinesDataView.value) {
            return;
        }

        const changedSharedListLinesQuantities: Dictionary<number> = {};
        for (const wishListLineId in productInfosByWishListLineId) {
            changedSharedListLinesQuantities[wishListLineId] = productInfosByWishListLineId[wishListLineId]!.qtyOrdered;
        }
        if (linesSelected()) {
            const selectedLines = wishListLinesDataView.value.filter(o => selectedWishListLineIds.indexOf(o.id) > -1);
            const linesToAdd = selectedLines.filter(
                o =>
                    o.quoteRequired ||
                    o.allowZeroPricing ||
                    productInfosByWishListLineId[o.id]?.pricing?.unitNetPrice !== 0,
            );

            if (linesToAdd.length === 0) {
                toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
                return;
            }

            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: {
                        cartLines: linesToAdd.map(o => {
                            return { ...o, qtyOrdered: changedSharedListLinesQuantities[o.id] } as any as CartLineModel;
                        }),
                    } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    onAddToCartSuccess({
                        notAllAddedToCart: linesToAdd.length !== selectedLines.length,
                    } as CartLineCollectionModel);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        } else {
            addWishListToCart({
                apiParameter: {
                    wishListId: wishList.id,
                    changedSharedListLinesQuantities,
                },
                onSuccess: onAddToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            });
        }
    };

    // <XNG-Changes>
    const linesSelected = () => {
        return selectedWishListLineIds.length > 0;
    };

    const enableRemoveSelected: boolean =
        (wishList.allowEdit || !wishList.isSharedList) && linesSelected() && !wishList.isAutogenerated;

    const removeSelectedClickHandler = () => {
        setDeleteListItemsModalIsOpen(true);
    };

    const removeSelectedCancelHandler = () => {
        setDeleteListItemsModalIsOpen(false);
    };

    const onRemoveSelectedSuccess = () => {
        toasterContext.addToast({
            body: translate(`Item${selectedWishListLineIds.length > 1 ? "s" : ""} Deleted`),
            messageType: "success",
        });
        setAllWishListLinesIsSelected({ isSelected: false });
    };

    const removeSelectedSubmitHandler = () => {
        if (!wishListDataView.value || !wishListLinesDataView.value) {
            return;
        }

        setDeleteListItemsModalIsOpen(false);
        const wishListLineIds = wishListLinesDataView.value
            .filter(o => selectedWishListLineIds.indexOf(o.id) > -1)
            .map(o => o.id);
        deleteWishListLines({
            wishListId: wishListDataView.value.id,
            wishListLineIds,
            onSuccess: onRemoveSelectedSuccess,
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    const displayToast = (message: React.ReactNode) => {
        toasterContext.addToast({ body: message, messageType: "success" });
    };

    const onAddToCartSuccess = (apiResult?: CartLineCollectionModel) => {
        displayToast(
            apiResult?.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : siteMessage("Cart_AllProductsAddedToCart"),
        );
    };

    const addToCartClickHandler = (e: any) => {
        e.preventDefault();
        if (!wishList || !wishListLinesDataView.value) {
            return;
        }
        if (linesSelected()) {
            const selectedLines = wishListLinesDataView?.value.filter(o => selectedWishListLineIds.indexOf(o.id) > -1);
            const linesToAdd = selectedLines.filter(
                o =>
                    o.quoteRequired ||
                    o.allowZeroPricing ||
                    productInfosByWishListLineId[o.id]?.pricing?.unitNetPrice !== 0,
            );

            if (linesToAdd.length === 0) {
                toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
                return;
            }

            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: {
                        cartLines: linesToAdd.map(o => o as any as CartLineModel),
                    } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    onAddToCartSuccess({
                        notAllAddedToCart: linesToAdd.length !== selectedLines.length,
                    } as CartLineCollectionModel);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    };
    const allQuantitiesAreValid = (wishListLines: WishListLineModel[]): boolean => {
        return wishListLines
            .filter(o => selectedWishListLineIds.length === 0 || selectedWishListLineIds.indexOf(o.id) > -1)
            .every(o => o.qtyOrdered && parseFloat(o.qtyOrdered.toString()) > 0);
    };
    const enableAddToCart = () => {
        const lines = wishListLinesDataView.value;
        return (
            wishList?.canAddToCart &&
            !isUpdatingCart &&
            lines?.some(o => canAddWishListLineToCart(o, productInfosByWishListLineId)) &&
            allQuantitiesAreValid(lines)
        );
    };

    const editingSortOrderChangeHandler = () => {
        editingSortOrder
            ? setEditingSortOrder({ editingSortOrder: false })
            : setEditingSortOrder({ editingSortOrder: true });
    };
    // const editingSortOrderChangeHandler: CheckboxProps["onChange"] = (_, value) => {
    //     setEditingSortOrder({ editingSortOrder: value });
    // };
    // </XNG-Changes>

    // const addItemsToListClickHandler = () => {
    //     setAddItemsToListIsOpen(true);
    // };

    const selectProductHandler = async (productInfo: ProductInfo) => {
        await addToWishList({ productInfos: [productInfo], selectedWishList: wishList });
        toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
    };

    //     const uploadItemsClickHandler = () => {
    //         setUploadItemsModalIsOpen(true);
    //     };
    //
    //     const uploadItemsModalCloseHandler = () => {
    //         setUploadItemsModalIsOpen(false);
    //     };

    // const uploadProductsHandler = async (products: ProductDto[]) => {
    //     const productInfos = products.map(o => ({
    //         productId: o.id,
    //         qtyOrdered: o.qtyOrdered,
    //         unitOfMeasure: o.selectedUnitOfMeasure ?? "",
    //     }));
    //     await addToWishList({ productInfos, selectedWishList: wishList });
    //     setUploadItemsModalIsOpen(false);
    //     loadWishListIfNeeded({ wishListId: wishList.id });
    // };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    // XNG-Changes: disable sort select when list is empty
    const disableActionsUntilLoadList =
        !wishListDataView.value ||
        !wishListLinesDataView.value ||
        wishListLinesDataView.isLoading ||
        !wishListLinesDataView.pagination;

    const isEmptyList = wishListLinesDataView && wishListLinesDataView.value && wishListLinesDataView.value.length <= 0;

    // XNG-Change
    const wishListCount = wishListLinesDataView?.value ? wishListLinesDataView?.pagination?.totalItemCount || 0 : null;
    const addListToCartButtonText = linesSelected() ? translate("Add Selected to Cart") : translate("Add List to Cart");

    // XNG-Change: changes on the structre - change the sort from button to be select
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.topSectionWrapper}>
                <StyledWrapper {...styles.listInfoWrapper}>
                    <StyledWrapper {...styles.itemsTotalWrapper}>
                        {!!wishListCount && wishListCount > 0 && (
                            <StyledWrapper {...styles.wishlistCountWrapper}>
                                <Typography {...styles.wishlistCount}>{wishListCount}</Typography>
                                <Typography {...styles.wishlistLabel}> {translate("items")}</Typography>
                            </StyledWrapper>
                        )}
                        {createWidgetElement("MyListsDetails/MyListsDetailsHeader", {
                            fields: {
                                showTotalOnly: true,
                            },
                        })}
                    </StyledWrapper>
                    <TextField
                        {...styles.search}
                        placeholder={translate("Search list by keyword or item #")}
                        value={query}
                        onChange={searchChangeHandler}
                        data-test-selector="wishListSearch"
                        // XNG-Changes
                        iconClickableProps={{}}
                        clearClickableProps={
                            query
                                ? {
                                      clickableProps: {
                                          onClick: () => {
                                              setQuery("");
                                          },
                                      },
                                  }
                                : undefined
                        }
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.innerButtonWrapper}>
                    {!wishList.isAutogenerated && <AddNewItems wishList={wishList} parentId={id} />}
                    <Button
                        key="addToCart"
                        disabled={!enableAddToCart() || !showAddToCart}
                        {...styles.addListButton}
                        onClick={addAllToCartClickHandler}
                        data-test-selector="myListDetails_addListToCart"
                    >
                        {addListToCartButtonText}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.bottomSectionWrapper}>
                <StyledWrapper {...styles.leftSectionWrapper}>
                    <Checkbox
                        {...styles.selectAll}
                        checked={isAllSelected}
                        onChange={selectAllChangeHandler}
                        data-test-selector="selectAllItems"
                        // XNG-Changes: disable mylist details checkboxes when list is empty
                        disabled={!wishListLinesDataView.value?.length}
                    >
                        {translate("Select All")}
                    </Checkbox>
                    {/** <XNG-Changes>:added to match the design */}
                    <StyledWrapper {...styles.addDeleteWrapper}>
                        <Button
                            disabled={!enableAddToCart() || !linesSelected()}
                            {...styles.addSelectedToCartButton}
                            onClick={addToCartClickHandler}
                            data-test-selector="myListDetails_addListToCart"
                        >
                            {translate("Add To Cart")}
                        </Button>
                        <Button
                            onClick={removeSelectedClickHandler}
                            disabled={!enableRemoveSelected}
                            {...styles.deleteSelectedButton}
                        >
                            {translate("Delete")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>

                {/** </XNG-Changes> */}
                <StyledWrapper {...styles.rightSectionWrapper}>
                    {/** XNG-Changes:use select to match the design */}
                    {!wishList.isAutogenerated && !wishList.isDynamic && (
                        <Select
                            label={translate("Sort By")}
                            labelPosition={isMobileScreen ? "top" : "left"}
                            value={loadWishListLinesParameter.sort}
                            onChange={event => sortByChangeHandler(event.target.value)}
                            disabled={disableActionsUntilLoadList || isEmptyList}
                            {...styles.sortBySelect}
                        >
                            {wishListLinesDataView.value &&
                                wishListLinesDataView.pagination?.sortOptions?.map(sortOption => (
                                    <option key={sortOption.sortType} value={sortOption.sortType}>
                                        {sortOption.displayName}
                                    </option>
                                ))}
                        </Select>
                    )}
                    {loadWishListLinesParameter.sort === "SortOrder" && !query && canEditWishList && (
                        <Link
                            {...styles.editSortOrderLink}
                            onClick={editingSortOrderChangeHandler}
                            data-test-selector="myListDetailPage_editSortOrderLink"
                            // XNG-Changes: disable mylist details edit sort link when list is empty
                            disabled={!wishListLinesDataView.value?.length}
                        >
                            {editSortOrderText}
                        </Link>
                    )}
                    {createWidgetElement("MyListsDetails/MyListDetailsPagination", {
                        fields: {},
                    })}
                </StyledWrapper>
            </StyledWrapper>

            {/* XNG-Changes: Moved ProductSelector to Actions */}
            {/*    <GridItem {...styles.addItemsGridItem}>
                            {canEditWishList && (
                                <Link
                                    {...styles.addItemsLink}
                                    onClick={addItemsToListClickHandler}
                                    data-test-selector="myListDetails_addItemsToList"
                                >
                                    {translate("Add Items To List")}
                                </Link>
                            )}
                        </GridItem>
                    </GridContainer>
                )}
             {addItemsToListIsOpen && (
                    <GridContainer {...styles.addItemsGridContainer}>
                        <GridItem {...styles.productSelectorGridItem}>
                            <ProductSelector
                                selectButtonTitle={translate("Add to List")}
                                skipInventoryValidation={true}
                                denyFixedConfiguration={true}
                                onSelectProduct={selectProductHandler}
                                productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                                productIsUnavailableMessage={siteMessage("Product_NotFound")}
                                extendedStyles={styles.productSelectorStyles}
                            />
                        </GridItem>
                        <GridItem {...styles.uploadItemsGridItem}>
                            <Link
                                {...styles.uploadItemsLink}
                                onClick={uploadItemsClickHandler}
                                data-test-selector="myListDetails_uploadItems"
                            >
                                {translate("Upload Items")}
                            </Link>
                            <Modal
                                {...styles.uploadItemsModal}
                                headline={translate("Upload Items To List")}
                                isOpen={uploadItemsModalIsOpen}
                                handleClose={uploadItemsModalCloseHandler}
                                data-test-selector="myListDetails_uploadItemsToListModal"
                            >
                                <GridContainer>
                                    <GridItem width={[12, 12, 6, 6, 6]}>
                                        <OrderUpload
                                            descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                            checkInventory={false}
                                            templateUrl="/Excel/ListUploadTemplate.xlsx"
                                            onUploadProducts={uploadProductsHandler}
                                            extendedStyles={styles.orderUploadStyles}
                                        />
                                        <OrderUploadErrorsModal
                                            descriptionText={siteMessage("ListUpload_UploadError")}
                                            uploadErrorText={siteMessage("ListUpload_UploadError")}
                                            rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                            errorReasons={{
                                                [UploadError.ConfigurableProduct]: siteMessage(
                                                    "ListUpload_CannotOrderConfigurable",
                                                ),
                                                [UploadError.StyledProduct]:
                                                    siteMessage("ListUpload_CannotOrderStyled"),
                                                [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                                [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                            }}
                                            extendedStyles={styles.orderUploadErrorsModalStyles}
                                        />
                                    </GridItem>
                                    <GridItem width={[12, 12, 6, 6, 6]}>
                                        <Zone zoneName="UploadInstructions" contentId={id} />
                                    </GridItem>
                                </GridContainer>
                            </Modal>
                        </GridItem>
                    </GridContainer>
                )}
        </StyledWrapper>
           <StyledWrapper {...styles.rightColumnWrapper} data-test-selector="myListDetailPage_ProductsSortBy">
                {!addItemsToListIsOpen && !wishList.isAutogenerated && !wishList.isDynamic && (
                    <OverflowMenu position="end" {...styles.sortByOverflowMenu}>
                        {wishListLinesDataView.value &&
                            wishListLinesDataView.pagination?.sortOptions?.map(sortOption => (
                                <Clickable
                                    {...(sortOption.sortType === loadWishListLinesParameter.sort
                                        ? styles.selectedSortByClickable
                                        : styles.sortByClickable)}
                                    key={sortOption.sortType}
                                    onClick={() => sortByChangeHandler(sortOption.sortType)}
                                >
                                    {sortOption.displayName}
                                </Clickable>
                            ))}
                    </OverflowMenu>
                )}
                {addItemsToListIsOpen && (
                    <Icon
                        {...styles.closeAddItemsToList}
                        onClick={() => {
                            setAddItemsToListIsOpen(false);
                        }}
                    />
                )}
            </StyledWrapper>
                */}
            <TwoButtonModal
                headlineText={translate(`Delete Item${selectedWishListLineIds.length > 1 ? "s" : ""}`)}
                {...styles.deleteListItemsModal}
                modalIsOpen={deleteListItemsModalIsOpen}
                messageText={translate(
                    `Are you sure you want to delete ${
                        selectedWishListLineIds.length > 1 ? "these items" : "this item"
                    }?`,
                )}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={removeSelectedCancelHandler}
                onSubmit={removeSelectedSubmitHandler}
                submitTestSelector="submitDeleteListItems"
            ></TwoButtonModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsOptions),
    definition: {
        group: "My Lists Details",
        displayName: "Options",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
